import React, { useState, useEffect, useRef, useContext } from "react";
import { GdkButtonSwitch } from "@gdk/button-switch";
import { updateSweepDetails, deleteSweepDetails } from '../../api/agency-profile';
import { useForm } from "react-hook-form";
import * as yup from 'yup';
import { yupResolver } from "@hookform/resolvers/yup";
import '@gdk/button-switch/dist/styles/gdk-button-switch.min.css';
import { GdkModal } from "@gdk/modal";
import '../../styles/Sweeps.css';
import '../../styles/SweepBranchEmail.css';
import EmailForm from "./EmailForm";
import ExpandableFormList from "../gdk/ExpandableForm/ExpandableFormList";
import ExpandableFormListItem from "../gdk/ExpandableForm/ExpandableFormListItem";
import { logDetails } from "../../api/logger-configuration";
import { UserContext } from '../../context/UserContextProvider';
import { publishSweepEvent } from "./PublishSweepEventRequestHelper";
import { ClientConfigurationContext } from '../../context/ClientConfigurationContextProvider';
import EmailManagement from './EmailManagement';
import BranchEmailManagement from "./BranchEmailManagement";
import Toast, { ToastTypes } from "../gdk/Toast/Toast";


// helper function to determine alert configuration
const getAlertConfiguration = (agencyUnenrolled) => {
    const heading = agencyUnenrolled ? "Unenrolled Agency" : "Agency Not Enrolled";
    const subHeading = agencyUnenrolled
        ? "This agency is no longer enrolled in sweep payment services. To re-enroll this agency, select the enroll button below."
        : "This agency is not enrolled in sweep payment services. To enroll this agency, select the enroll button below.";
    return { heading, subHeading };
};

// helper function to check if details should be shown
const shouldShowDetails = (showAlert, agencyProfile) => {
    return !showAlert && (!agencyProfile?.sweepDetails?.IsPrincipleDeactivated || !agencyProfile?.sweepDetails?.IsSweepsEnrolled);
};

// helper function to check if email management should be shown
const shouldShowEmailManagement = (clientConfiguration, agencyProfile) => {
    return clientConfiguration?.enableEmailSettings &&
        agencyProfile?.sweepDetails?.SweepsEmail &&
        agencyProfile?.sweepDetails?.IsSweepsEnrolled;
};

// helper function to render email management section
const renderEmailManagementSection = ({ clientConfiguration, agencyProfile, setAgencyProfile, setAgencyBranches, resetTrigger, isBranch, setEmailToDelete, setShowDeleteModal }) => {
    if (!shouldShowEmailManagement(clientConfiguration, agencyProfile)) {
        return null;
    }

    if (isBranch) {
        return (
            <div>
                <h3 className="header-padding">
                    {agencyProfile?.name} Email Settings
                    <gds-tooltip>You can add up to 5 email addresses in addition to the branch primary email.</gds-tooltip>
                </h3>
                <div>
                    <BranchEmailManagement
                        agencyProfile={agencyProfile}
                        setAgencyBranches={setAgencyBranches}
                        resetTrigger={resetTrigger}
                        setEmailToDelete={setEmailToDelete}
                        setShowDeleteModal={setShowDeleteModal}
                    />
                </div>
            </div>
        );
    }

    return (
        <div>
            <h3 className="header-padding">
                Agency Email Settings
                <gds-tooltip>You can add up to 5 email addresses in addition to the agency primary email.</gds-tooltip>
            </h3>
            <div>
                <EmailManagement
                    agencyProfile={agencyProfile}
                    setAgencyProfile={setAgencyProfile}
                    setEmailToDelete={setEmailToDelete}
                    setShowDeleteModal={setShowDeleteModal}
                />
            </div>
        </div>
    );
};

const SweepDetailsContainer = ({ agencyProfile, setAgencyProfile, isPrinciple = false, isBranch = false, setToggleUpdate, agencyBranches, setAgencyBranches, resetTrigger, onClickAddOrDelete, isLoading }) => {
    const user = useContext(UserContext);
    const clientConfiguration = useContext(ClientConfigurationContext);
    const gdkButtonSwitch = useRef(null);
    const [isDisabled, setIsDisabled] = useState(false);
    const [showAlert, setShowAlert] = useState(false);
    const [agencyUnenrolled, setAgencyUnenrolled] = useState(false);
    const branchStatusChip = agencyProfile?.sweepDetails?.IsPrincipleDeactivated ? "INActive" : "Active";
    const handleToggle = () => {
        setToggleUpdate(prev => !prev);
    };

    const [emailEditing, setEmailEditing] = useState(false);
    const [sweepEmailExpandableForm, setSweepEmailExpandableForm] = useState();
    const gdkModal = useRef(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const deleteEmailConfirmationToastRef = useRef(null);

    const validationSchema = yup.object();

    const { register, handleSubmit, formState: { errors }, reset } = useForm({ resolver: yupResolver(validationSchema) });

    const handleEmailEditClick = () => {
        setEmailEditing(true);
        setIsDisabled(true);
    }

    const onClickDelete = () => {
        setShowDeleteModal(true);
    }

    const deleteSweepAccount = (agency, isPrinciple) => {
        // Replace null ivans with empty object as required by APIs
        agency = {
            ...agency,
            ivans: agency.ivans ?? {}
        };

        deleteSweepDetails(agency,
            () => {
                const updatedProfile = {
                    ...agency
                };
                updatedProfile.sweepDetails.IsPrincipleDeactivated = false;
                updatedProfile.sweepDetails.SweepsPaymentMethodId = null;
                updatedProfile.sweepDetails.AccountNickname = null;
                updatedProfile.sweepDetails.Last4AccountNumber = null;
                updatedProfile.sweepDetails.IsSweepsEnrolled = false;
                updatedProfile.sweepDetails.SweepsEmail = null;
                updatedProfile.sweepDetails.AdditionalSweepEmails = [];
                updateSweepDetails(updatedProfile,
                    () => {
                        logDetails(`Saved agency profile with sweep details deleted.`)
                        publishSweepEvent(updatedProfile.agencyId,
                            "PrimaryAgencyUnEnrolled",
                            user?.agentId,
                            "Sweep event published successfully for agency unenrolled."
                        );
                        setAgencyUnenrolled(true);
                        handleToggle();
                    },
                    () => {

                    }
                );
                setShowDeleteModal(false);
            },
            () => {
                setShowDeleteModal(false);
                setIsDisabled(false);
            }
        );
    }

    const onSubmit = (values) => {

        setIsDisabled(true);
        deleteSweepAccount(agencyProfile, true);

        if (values.deleteBranches === "true") {
            agencyBranches.forEach((branch) => {
                if (branch?.sweepDetails?.SweepsPaymentMethodId) {
                    deleteSweepAccount(branch, false);
                }
            });
        }
    }

    const onCancel = () => {
        reset();
        setShowDeleteModal(false);
    };

    const handleSave = () => {
        setEmailEditing(false);
        setIsDisabled(false);
    };

    const handleCancel = () => {
        setEmailEditing(false);
        setIsDisabled(false);
    };

    useEffect(() => {
        setEmailEditing(false);
        setIsDisabled(false);
    }, [resetTrigger]);

    useEffect(() => {
        gdkModal.deleteSweepAccount = new GdkModal({
            content: `#agency-sweep-deletion-modal-${agencyProfile?.agencyId}`,
            hideCloseButton: false,
            onClosed: () => {
                setShowDeleteModal(false);
            },
        });
    }, []);

    useEffect(() => {
        if (showDeleteModal === true && gdkModal.deleteSweepAccount?.show) {
            gdkModal.deleteSweepAccount.show();
        }

        if (showDeleteModal === false && gdkModal.deleteSweepAccount?.hide) {
            gdkModal.deleteSweepAccount.hide();
        }
    }, [showDeleteModal]);

    useEffect(() => {
        if (isPrinciple) {
            gdkButtonSwitch.current = new GdkButtonSwitch({
                content: "#principle-agency-toggle",
                initiallyChecked: isPrinciple && !agencyProfile?.sweepDetails?.IsPrincipleDeactivated === true,
                onSwitchChange: function (buttonSwitchCheckedState) {
                    handleDisableParent(buttonSwitchCheckedState, agencyProfile);
                }
            });
        }
    }, []);

    const handleDisableParent = (isDeactivated, agencyBranch) => {
        setIsDisabled(true);
        const updatedAgencyProfile = {
            ...agencyBranch,
            ivans: agencyBranch.ivans ?? {},
            sweepDetails: agencyBranch.sweepDetails ? {
                ...agencyBranch.sweepDetails,
                IsPrincipleDeactivated: !isDeactivated
            } : { IsPrincipleDeactivated: !isDeactivated }
        };

        const eventType = isDeactivated ? "PrimaryAgencyEnabled" : "PrimaryAgencyDisabled";
        const message = isDeactivated
            ? "Sweep event published successfully for agency activation."
            : "Sweep event published successfully for agency deactivation.";

        updateSweepDetails(updatedAgencyProfile,
            () => {
                setAgencyProfile(updatedAgencyProfile);
                setShowAlert(!isDeactivated);
                setIsDisabled(false);
                publishSweepEvent(updatedAgencyProfile.agencyId, eventType, user?.agentId, message);
                handleToggle();
            },
            () => {
                setIsDisabled(false);
            }
        );
    };

    const AlertLow = ({ setShowAlert, isClosable = false, heading, subHeading }) => (
        <div id="alert-low" className="alert">
            <div className="alert--low-importance">
                <span className="icon-alert" />
                <ul className="alert-list">
                    <li className="alert-content">
                        {isClosable && <button aria-label="Close alert" className="icon-close" onClick={() => setShowAlert(false)}></button>}
                        <div className="h4">{heading}</div>
                        <p>
                            {subHeading}
                        </p>
                    </li>
                </ul>
            </div>
        </div>
    );

    return (
        <div className="sweeps">
            {deleteEmailConfirmationToastRef && (
                <Toast
                    toastRef={deleteEmailConfirmationToastRef}
                    header="Email Deleted"
                    description="Email address has been removed"
                    toastType={ToastTypes.Confirmation}
                />
            )}
            <div className={isPrinciple ? "flexible-container" : ""}>
                {(isPrinciple &&
                    <div>
                        <div className="header-container">
                            <div>
                                <h4 className="h4">
                                    {agencyProfile?.name}
                                </h4>
                                <div className={`chips ${branchStatusChip === 'INActive' ? 'chips-neutral' : 'chips-success'}`}>{branchStatusChip}</div>
                                {!emailEditing &&
                                    <span className="geico-icon--small geico-icon--actionable geico-icon icon-trash header-trash-icon" onClick={onClickDelete} ></span>}
                            </div>
                        </div>
                        <br />
                        <div id="principle-agency-toggle" className={`button-switch-container ${isDisabled ? 'disabled' : ''}`}>
                            <div className="button-switch-label-wrapper">
                                <label htmlFor={"principle-agency-toggle"} className="text">Agency Sweep Account
                                    <gds-tooltip>You can activate or deactivate this agency sweep account by selecting the toggle switch.</gds-tooltip>
                                </label>
                                <span slot="descriptive-copy">
                                    {(agencyProfile?.sweepDetails?.Last4AccountNumber) ? `Account Number: ******${agencyProfile?.sweepDetails?.Last4AccountNumber}` : ""}
                                </span>
                            </div>
                            <input id={"principle-agency-toggle"} name={"principle-agency-toggle"} type="checkbox" />
                            <div className="button-switch"></div>
                        </div>
                        <span className="stroke-separator"></span>
                    </div>
                )}
                {shouldShowDetails(showAlert, agencyProfile) && (
                    <div className="flexible-container">
                        <h4>
                            {isPrinciple ? "Account Details" : isBranch ? "Sweep Account Details" : agencyProfile?.name}
                        </h4>
                        <ul className="list list--two-column" >
                            {isBranch && (
                                <li>
                                    <div>
                                        <label htmlFor="branch-sweep-account" className="col-sm-10">Branch Sweep Account
                                            <gds-tooltip>By default, all branches use the same sweep account as their agency. However, you can add or remove a new sweep account exclusive to a branch.</gds-tooltip>
                                            <p>{agencyProfile?.sweepDetails?.Last4AccountNumber && `Account Number: ******${agencyProfile?.sweepDetails?.Last4AccountNumber}`}</p>
                                        </label>
                                    </div>
                                    <div slot="right-column-content" >
                                        <button type="button" className={`gds-link button-to-link-unenroll`} onClick={onClickAddOrDelete} disabled={isLoading} >
                                            {"Unenroll"}
                                        </button>
                                    </div>
                                </li>
                            )}
                            {agencyProfile?.sweepDetails?.AccountNickname ? (
                                <li>
                                    <div>
                                        <div className="col-sm-10">Account Nickname</div>
                                    </div>
                                    <div slot="right-column-content">
                                        {agencyProfile?.sweepDetails?.AccountNickname}
                                    </div>
                                </li>
                            ) : (!agencyProfile?.sweepDetails?.IsSweepsEnrolled &&
                                (<li>
                                    <div>
                                        <div className="col-sm-10">Account Nickname</div>
                                    </div>
                                    <div slot="right-column-content">
                                        None
                                    </div>
                                </li>)
                            )}
                            {agencyProfile?.sweepDetails?.Last4AccountNumber ? (
                                <li>
                                    <div>
                                        <div className="col-sm-10">Bank Account Number</div>
                                    </div>
                                    <div slot="right-column-content">
                                        ******{agencyProfile?.sweepDetails?.Last4AccountNumber}

                                    </div>

                                </li>
                            ) : (!agencyProfile?.sweepDetails?.IsSweepsEnrolled && (
                                <li>
                                    <div>
                                        <div className="col-sm-10">Bank Account Number</div>
                                    </div>
                                    <div slot="right-column-content">
                                        None
                                    </div>
                                </li>)
                            )}
                            {!clientConfiguration.enableEmailSettings && (
                                <li>
                                    <div>
                                        <div className="col-sm-10">
                                            Email
                                        </div>
                                        {agencyProfile?.sweepDetails?.SweepsEmail && (
                                            <div className="col-sm-2 email-edit-link">
                                                <a slot="right-column-content" onClick={handleEmailEditClick}>Edit</a>
                                            </div>
                                        )}
                                    </div>
                                    {emailEditing ? (
                                        <ExpandableFormList setExpandableForm={setSweepEmailExpandableForm}>
                                            <ExpandableFormListItem
                                                id="email-form-li"
                                                headline="Email"
                                                editable={false}
                                                form={<EmailForm
                                                    onSave={handleSave}
                                                    onCancel={handleCancel}
                                                    expandableForm={sweepEmailExpandableForm}
                                                    agencyProfile={agencyProfile}
                                                    setAgencyProfile={setAgencyProfile}
                                                    branchProfile={agencyProfile}
                                                    setAgencyBranches={setAgencyBranches}
                                                    isBranch={isBranch}
                                                />}
                                            />
                                        </ExpandableFormList>
                                    ) : (
                                        <div slot="right-column-content">

                                            <div className="right-side">
                                                {agencyProfile?.sweepDetails?.SweepsEmail || "None"}
                                            </div>
                                        </div>
                                    )}
                                </li>
                            )}
                        </ul>
                        {!agencyProfile?.sweepDetails?.IsSweepsEnrolled && (() => {
                            const { heading, subHeading } = getAlertConfiguration(agencyUnenrolled);
                            return (
                                <AlertLow
                                    setShowAlert={setShowAlert}
                                    heading={heading}
                                    subHeading={subHeading}
                                />
                            );
                        })()}
                    </div>
                )}
                {renderEmailManagementSection({
                    clientConfiguration,
                    agencyProfile,
                    setAgencyProfile,
                    setAgencyBranches,
                    resetTrigger,
                    isBranch,
                    setShowDeleteModal,
                })}
                {showAlert && (
                    <AlertLow setShowAlert={setShowAlert} isClosable={true} heading="Inactive Agency Sweep Account" subHeading="This account is Inactive. You can activate it by selecting the toggle switch above." />
                )}
            </div>
            <div id={`agency-sweep-deletion-modal-${agencyProfile?.agencyId}`} className="modal">
                <div className="modal-container">
                    <h2 className="modal-headline">Unenroll this agency?</h2>
                    <div className="full-width-line" />
                    <div className="modal-body">
                        {agencyBranches?.length > 0
                            ? "If you continue, this agency will be unenrolled from sweep payment services and all payment information will be removed. Please select whether to keep or delete the branches connected to this account below."
                            : "If you continue, this agency will be unenrolled from sweep payment services and all payment information will be removed."
                        }
                    </div>
                    <div className="modal-content">
                        <form onSubmit={handleSubmit(onSubmit)}>
                            {agencyBranches?.length > 0 && (
                                <fieldset role="radiogroup" className="form-field radio-button-group">
                                    <legend>Do you want to delete branches connected to this account?</legend>
                                    <div className="radio-button-wrapper col-1">
                                        <div className="label--description">
                                            <input
                                                id="radio1"
                                                type="radio"
                                                name="radio1"
                                                value="true"
                                                {...register("deleteBranches")}
                                            />
                                            <label className="radio" htmlFor="radio1">
                                                <span>Delete Branches</span>
                                            </label>
                                        </div>
                                        <div>
                                            <input
                                                id="radio2"
                                                type="radio"
                                                name="radio1"
                                                value="false"
                                                defaultChecked
                                                {...register("deleteBranches")}
                                            />
                                            <label className="radio" htmlFor="radio2">
                                                <span>Keep Branches</span>
                                            </label>
                                        </div>
                                    </div>
                                </fieldset>
                            )}
                            <div className="modal-content">
                                <div className="modal--call-to-action-bar">
                                    <button
                                        type="submit"
                                        className="btn btn--primary btn--full-mobile btn--pull-right"
                                        data-testid="modal-delete-branches-button"
                                        disabled={isDisabled}
                                    >
                                        <span>Continue</span>
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn--destructive btn--full-mobile btn--pull-right"
                                        data-testid="modal-cancel-delete-branches-button"
                                        onClick={() => onCancel()}
                                    >
                                        <span>Cancel</span>
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SweepDetailsContainer;